import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  SegmentedControl,
  Skeleton,
  useToast,
} from '@ntconsult/aluma-react';
import { Plus, SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ALLOCATION_COLUMN_LABEL_KEYS, useAllocationColumns } from '../hooks/useAllocationColumns';
import { useAllocationFilters } from '../hooks/useAllocationFilters';
import { useAllocationSelection } from '../hooks/useAllocationSelection';
import { useAllocations } from '../hooks/useAllocations';
import { useComplementarySearch } from '../hooks/useComplementarySearch';
import { useEmptyScope } from '../hooks/useEmptyScope';
import { useExportAllocations } from '../hooks/useExportAllocations';
import { toApiDate } from '../utils/data';
import { i18nKeyForError, isSessionError, parseApiError } from '../utils/errors';
import { AdvancedFiltersDrawer } from './AdvancedFiltersDrawer';
import { AllocationModal, type EditAllocationTarget } from './AllocationModal';
import { AllocationsTable } from './AllocationsTable';
import { AllocationsTimeline } from './AllocationsTimeline';
import { AppliedFilterChips } from './AppliedFilterChips';
import { BatchTagsBar } from './BatchTagsBar';
import { BatchTagsModal } from './BatchTagsModal';
import { CloseAllocationModal, type CloseAllocationTarget } from './CloseAllocationModal';
import { ColumnsPicker } from './ColumnsPicker';
import { ExportButton } from './ExportButton';
import { NoIdentityState } from './NoIdentityState';
import { PINNED_BLOCK, WIDE_PAGE } from './wideTable';

type ModalTarget =
  | { type: 'new' }
  | { type: 'edit'; target: EditAllocationTarget }
  | { type: 'view'; target: EditAllocationTarget }
  | null;

type View = 'tabela' | 'linhaDoTempo';

type AllocationsPageProps = {
  multiAllocated?: boolean;
};

export function AllocationsPage({ multiAllocated = false }: AllocationsPageProps) {
  const { t } = useTranslation('alocacao');
  const navigate = useNavigate();
  const { toast } = useToast();
  const filterState = useAllocationFilters();
  const filters = { ...filterState.filters, multiAllocated };
  const title = multiAllocated ? t('consultas.multialocados.titulo') : t('titulo');
  const columnsState = useAllocationColumns();
  const exportAllocations = useExportAllocations(filters, columnsState.visibleColumns);

  const [view, setView] = useState<View>('tabela');
  const allocationsQuery = useAllocations(filters, { enabled: view === 'tabela' });
  const scope = useEmptyScope();

  const [modalTarget, setModalTarget] = useState<ModalTarget>(null);
  const [closeTarget, setCloseTarget] = useState<CloseAllocationTarget | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [batchTagsOpen, setBatchTagsOpen] = useState(false);

  const selection = useAllocationSelection(JSON.stringify(filters));
  const selectedAllocations = (allocationsQuery.data?.professionals ?? [])
    .flatMap((professional) => professional.allocations)
    .filter((allocation) => selection.isSelected(allocation.id));

  const { pending: countsPending, counts } = useComplementarySearch(filters);
  const otherScreenUrl = filterState.searchUrlFor(
    multiAllocated ? '/' : '/consultas/multialocados',
  );

  // Todos os pesquisados estão na outra tela: a pesquisa vai para lá.
  const searchBelongsElsewhere = counts !== null && counts.here === 0 && counts.elsewhere > 0;
  useEffect(() => {
    if (!searchBelongsElsewhere) return;
    toast.info({
      title: t(
        multiAllocated
          ? 'consultas.multialocados.pesquisaAbertaEmAlocacoes'
          : 'consultas.multialocados.pesquisaAbertaEmMultialocados',
      ),
    });
    navigate(otherScreenUrl, { replace: true });
  }, [searchBelongsElsewhere, multiAllocated, otherScreenUrl, navigate, toast, t]);

  const error = parseApiError(allocationsQuery.error);
  const isListLoading =
    allocationsQuery.isLoading ||
    allocationsQuery.isPlaceholderData ||
    countsPending ||
    searchBelongsElsewhere;

  const canCreate = !scope.loading && !scope.empty;

  return (
    <div className={`flex flex-col gap-6 ${WIDE_PAGE}`}>
      <div className={PINNED_BLOCK}>
        <PageHeader
          title={title}
          description={multiAllocated ? t('consultas.multialocados.descricao') : t('descricao')}
          actions={
            canCreate && (
              <Button
                variant="primary"
                leftIcon={<Plus size={16} />}
                onClick={() => setModalTarget({ type: 'new' })}
              >
                {t('nova')}
              </Button>
            )
          }
        />
      </div>

      <section className="flex min-w-0 flex-col gap-4" aria-label={title}>
        {scope.loading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {!scope.loading && scope.empty && (
          <EmptyState
            title={t('estados.escopoVazioTitulo')}
            description={t('estados.escopoVazioDescricao')}
          />
        )}

        {!scope.loading && !scope.empty && (
          <>
            <div className={`flex items-center gap-2 ${PINNED_BLOCK}`}>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {view === 'tabela' && allocationsQuery.data && (
                  <span className="text-sm text-foreground-muted" aria-live="polite">
                    {t('filtros.totalProfissionais', {
                      count: allocationsQuery.data.pagination.totalProfessionals,
                      showing: allocationsQuery.data.professionals.length,
                    })}
                  </span>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<SlidersHorizontal size={14} />}
                  onClick={() => setAdvancedOpen(true)}
                >
                  {t('filtros.avancados')}
                </Button>
                {view === 'tabela' && (
                  <ColumnsPicker
                    columnsState={columnsState}
                    labelKeys={ALLOCATION_COLUMN_LABEL_KEYS}
                  />
                )}
                {view === 'tabela' && (
                  <ExportButton
                    exportMutation={exportAllocations}
                    disabled={!allocationsQuery.data?.pagination.totalProfessionals}
                    labels={{
                      title: t('exportacao.titulo'),
                      allPages: t('exportacao.todasPaginas', {
                        count: allocationsQuery.data?.pagination.totalProfessionals ?? 0,
                      }),
                      currentPage: t('exportacao.paginaAtual', {
                        count: allocationsQuery.data?.professionals.length ?? 0,
                      }),
                    }}
                  />
                )}
              </div>
              <SegmentedControl
                className="ms-auto shrink-0"
                value={view}
                onValueChange={(value) => setView(value as View)}
                aria-label={t('linhaDoTempo.alternarVisao')}
              >
                <SegmentedControl.Item value="tabela">
                  {t('linhaDoTempo.tabela')}
                </SegmentedControl.Item>
                <SegmentedControl.Item value="linhaDoTempo">
                  {t('linhaDoTempo.titulo')}
                </SegmentedControl.Item>
              </SegmentedControl>
            </div>

            <div className={PINNED_BLOCK}>
              <AppliedFilterChips filterState={filterState} />
            </div>

            {view === 'tabela' && selectedAllocations.length > 0 && (
              <div className={PINNED_BLOCK}>
                <BatchTagsBar
                  count={selectedAllocations.length}
                  onManageTags={() => setBatchTagsOpen(true)}
                  onClear={selection.clear}
                />
              </div>
            )}

            {counts && counts.here > 0 && counts.elsewhere > 0 && (
              <Alert
                variant="info"
                className={PINNED_BLOCK}
                title={t(
                  multiAllocated
                    ? 'consultas.multialocados.pesquisadosEmAlocacoes'
                    : 'consultas.multialocados.pesquisadosEmMultialocados',
                  { count: counts.elsewhere },
                )}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => navigate(otherScreenUrl)}
                >
                  {t(
                    multiAllocated
                      ? 'consultas.multialocados.verEmAlocacoes'
                      : 'consultas.multialocados.verEmMultialocados',
                  )}
                </Button>
              </Alert>
            )}

            {view === 'tabela' ? (
              <>
                {isListLoading && (
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                )}

                {!isListLoading && allocationsQuery.isError && error && isSessionError(error) && (
                  <NoIdentityState code={error.code} />
                )}

                {!isListLoading && allocationsQuery.isError && !isSessionError(error) && (
                  <Alert
                    variant="danger"
                    title={t((error ? i18nKeyForError(error.code) : 'erros.unknown') as never)}
                  />
                )}

                {!isListLoading && !allocationsQuery.isError && allocationsQuery.data && (
                  <AllocationsTable
                    professionals={allocationsQuery.data.professionals}
                    filterState={filterState}
                    columnsState={columnsState}
                    page={filters.page}
                    perPage={filterState.perPage}
                    totalPages={Math.max(
                      1,
                      Math.ceil(
                        allocationsQuery.data.pagination.totalProfessionals /
                          allocationsQuery.data.pagination.perPage,
                      ),
                    )}
                    onPerPageChange={filterState.setPerPage}
                    onView={(target) => setModalTarget({ type: 'view', target })}
                    onEdit={(target) => setModalTarget({ type: 'edit', target })}
                    onCloseRequest={setCloseTarget}
                    groupByProfessional={multiAllocated}
                    selection={selection}
                  />
                )}
              </>
            ) : (
              <AllocationsTimeline
                professionalIds={filters.professionalIds}
                projectId={filters.projectId}
                contractId={filters.contractId}
                clientId={filters.clientId}
                projectSearch={filters.projectSearch}
                contractSearch={filters.contractSearch}
                clientSearch={filters.clientSearch}
                accountDirectorSearch={filters.accountDirectorSearch}
                operationManagerSearch={filters.operationManagerSearch}
                projectManagerSearch={filters.projectManagerSearch}
                tagIds={filters.tagIds}
                billable={filters.billable}
                minPercentage={filters.minPercentage}
                maxPercentage={filters.maxPercentage}
                notes={filters.notes}
                multiAllocated={multiAllocated}
                startDate={filterState.startDate}
                endDate={filterState.endDate}
                today={allocationsQuery.data?.today}
                onClearFilters={filterState.hasActiveFilters ? filterState.clearFilters : undefined}
                page={filterState.timelinePage}
                onPageChange={filterState.goToTimelinePage}
                perPage={filterState.perPage}
                onPerPageChange={filterState.setPerPage}
                timelineSpan={filterState.timelineSpan}
                onSpanChange={filterState.setTimelineSpan}
                timelineStatus={filterState.timelineStatus}
                onStatusChange={filterState.setTimelineStatus}
                onView={(target) => setModalTarget({ type: 'view', target })}
              />
            )}
          </>
        )}
      </section>

      <AdvancedFiltersDrawer
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        filterState={filterState}
        isColumnVisible={columnsState.isVisible}
        multiAllocated={multiAllocated}
      />

      {/* Renderiza quando há dados da tabela OU um alvo (clique numa barra da linha do tempo, onde a query da tabela fica desabilitada). */}
      {(allocationsQuery.data || modalTarget) && (
        <AllocationModal
          open={modalTarget !== null}
          onClose={() => setModalTarget(null)}
          target={modalTarget && modalTarget.type !== 'new' ? modalTarget.target : null}
          today={allocationsQuery.data?.today ?? toApiDate(new Date()) ?? ''}
          readOnly={modalTarget?.type === 'view'}
        />
      )}

      <BatchTagsModal
        open={batchTagsOpen}
        onClose={() => setBatchTagsOpen(false)}
        onApplied={selection.clear}
        allocations={selectedAllocations}
      />

      {allocationsQuery.data && (
        <CloseAllocationModal
          open={closeTarget !== null}
          onClose={() => setCloseTarget(null)}
          target={closeTarget}
          today={allocationsQuery.data.today}
        />
      )}
    </div>
  );
}
